import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

interface Message {
  id: string;
  content: string;
  senderId: string;
  receiverId: string;
  read: boolean;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  receiver: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Conversation {
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  lastMessage: Message;
  unreadCount: number;
  property?: {
    id: string;
    title: string;
    address: string;
    ownerName?: string;
  };
}

interface MessagingState {
  conversations: Conversation[];
  messages: Message[];
  currentConversation: Conversation | null;
  loading: boolean;
  error: string | null;
  unreadCount: number;
}

type MessagingAction =
  | { type: 'SET_CONVERSATIONS'; payload: Conversation[] }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'SET_CURRENT_CONVERSATION'; payload: Conversation | null }
  | { type: 'MARK_MESSAGES_READ'; payload: string[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_UNREAD_COUNT'; payload: number };

const initialState: MessagingState = {
  conversations: [],
  messages: [],
  currentConversation: null,
  loading: false,
  error: null,
  unreadCount: 0
};

const messagingReducer = (state: MessagingState, action: MessagingAction): MessagingState => {
  switch (action.type) {
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };
    
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    
    case 'ADD_MESSAGE':
      if (!action.payload || !action.payload.senderId || !action.payload.receiverId) {
        console.error('Invalid message payload:', action.payload);
        return state;
      }
      return {
        ...state,
        messages: [...state.messages, action.payload],
        conversations: state.conversations.map(conv => 
          conv.userId === action.payload.senderId || conv.userId === action.payload.receiverId
            ? { ...conv, lastMessage: action.payload }
            : conv
        )
      };
    
    case 'SET_CURRENT_CONVERSATION':
      return { ...state, currentConversation: action.payload };
    
    case 'MARK_MESSAGES_READ':
      return {
        ...state,
        messages: state.messages.map(msg => 
          action.payload.includes(msg.id) ? { ...msg, read: true } : msg
        ),
        conversations: state.conversations.map(conv => 
          conv.userId === state.currentConversation?.userId
            ? { ...conv, unreadCount: Math.max(0, conv.unreadCount - action.payload.length) }
            : conv
        ),
        unreadCount: Math.max(0, state.unreadCount - action.payload.length)
      };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };
    
    default:
      return state;
  }
};

const API_BASE = '/api';

const MessagingContext = createContext<{
  state: MessagingState;
  sendMessage: (receiverId: string, content: string, propertyId?: string) => Promise<void>;
  loadConversations: () => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  markAsRead: (messageIds: string[]) => Promise<void>;
  setCurrentConversation: (conversation: Conversation | null) => void;
  loadUnreadCount: () => Promise<void>;
} | null>(null);

export const MessagingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(messagingReducer, initialState);
  const { isAuthenticated, token, logout } = useAuth();

  const makeRequest = async (endpoint: string, options: RequestInit = {}) => {
    if (!token) {
      throw new Error('No authentication token');
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const code = (errorData as { code?: string }).code;
      // Only react to JWT failures — not generic 403 (e.g. permission). Never reload (reload loops).
      const jwtFailed =
        code === 'INVALID_TOKEN' ||
        code === 'TOKEN_EXPIRED' ||
        code === 'AUTH_ERROR';
      if (jwtFailed) {
        logout();
      }
      throw new Error((errorData as { error?: string }).error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const loadConversations = async () => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const data = await makeRequest('/messages/conversations');
      
      // Debug: Log the conversations data received from server
      console.log('Conversations data received from server:', data.data.map((conv: any) => ({
        userId: conv.userId,
        userName: `${conv.user?.firstName} ${conv.user?.lastName}`,
        property: conv.property ? {
          id: conv.property.id,
          title: conv.property.title
        } : 'No property'
      })));
      
      dispatch({ type: 'SET_CONVERSATIONS', payload: data.data });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversations';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      console.error('Error loading conversations:', err);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadMessages = async (conversationId: string) => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const data = await makeRequest(`/messages/conversations/${conversationId}/messages`);
      dispatch({ type: 'SET_MESSAGES', payload: data.data });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load messages';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      console.error('Error loading messages:', err);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const sendMessage = async (receiverId: string, content: string, propertyId?: string) => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: 'SET_ERROR', payload: null });
      const payload: any = { receiverId, content };
      if (propertyId) payload.propertyId = propertyId;

      const data = await makeRequest('/messages/send', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (data && data.message) {
        dispatch({ type: 'ADD_MESSAGE', payload: data.message });
      } else {
        console.error('Invalid message data received:', data);
        throw new Error('Invalid message data received from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      console.error('Error sending message:', err);
      throw err;
    }
  };

  const markAsRead = async (messageIds: string[]) => {
    if (!isAuthenticated || messageIds.length === 0) return;

    try {
      await makeRequest('/messages/mark-read', {
        method: 'PUT',
        body: JSON.stringify({ messageIds }),
      });

      dispatch({ type: 'MARK_MESSAGES_READ', payload: messageIds });
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  const loadUnreadCount = async () => {
    if (!isAuthenticated) return;

    try {
      const data = await makeRequest('/messages/unread-count');
      dispatch({ type: 'SET_UNREAD_COUNT', payload: data.data.unreadCount });
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const setCurrentConversation = (conversation: Conversation | null) => {
    dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: conversation });
  };

  // Load conversations and unread count on authentication
  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
      loadUnreadCount();
    } else {
      dispatch({ type: 'SET_CONVERSATIONS', payload: [] });
      dispatch({ type: 'SET_MESSAGES', payload: [] });
      dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: null });
      dispatch({ type: 'SET_UNREAD_COUNT', payload: 0 });
    }
    // loadConversations and loadUnreadCount are defined inside component; adding them would cause infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Auto-mark messages as read when viewing a conversation
  useEffect(() => {
    if (state.currentConversation && state.messages.length > 0) {
      const unreadMessages = state.messages.filter(
        msg => !msg.read && msg.senderId === state.currentConversation!.userId
      );
      if (unreadMessages.length > 0) {
        const unreadIds = unreadMessages.map(msg => msg.id);
        // Use a ref to prevent multiple calls
        const timeoutId = setTimeout(() => {
          markAsRead(unreadIds);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
    // markAsRead, state.currentConversation, state.messages intentionally omitted to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentConversation?.userId, state.messages.length]);

  const value = {
    state,
    sendMessage,
    loadConversations,
    loadMessages,
    markAsRead,
    setCurrentConversation,
    loadUnreadCount,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
}; 